import { Injectable } from '@angular/core';
import { UrlService } from '../url/url.service';
import * as Objects from '@core/objects';

@Injectable({
  providedIn: 'root'
})
export class FileDownloadService {
  constructor(private urlSrv: UrlService) {}

  /**
   * Download a file using the AmsFile uuid and signature
   * 
   * Endpoint: GET /pub/f/{uuid}?signature={signature}&...
   * 
   * @param file The AmsFile to download
   * @param options Optional download options
   * @param options.name Optional custom filename (without extension)
   * @param options.disposition How to serve the file: 'inline' or 'attachment' (default: 'attachment')
   * @param options.redirect Whether to redirect to presigned URL (default: true)
   * 
   * Flow:
   * 1. Validate file has required uuid and signature
   * 2. Check if signature is not expired
   * 3. Build URL with query parameters
   * 4. Trigger download via window.location.href
   */
  downloadFile(file: Objects.AmsFile, options?: {name?: string, disposition?: string, redirect?: boolean}): void {
    // Validate file has required properties
    if (!file || !file.uuid) {
      return;
    }

    // Check if file has access signature
    if (!file.access || !file.access.signature) {
      return;
    }

    // Check if signature is expired
    if (this.isSignatureExpired(file.access.expires_at)) {
      // TODO: emit event to parent to refresh signatures
      return;
    }

    // Build download URL
    const downloadUrl = this.buildDownloadUrl(file, options);
    
    // console.log('$$$$$ [FileDownload] Downloading file:', file.name, 'URL:', downloadUrl);
    
    // Trigger download
    window.location.href = downloadUrl;
  }

  /**
   * Download a version artwork asset (an AmsFile obtained from a version's `/assets` endpoint).
   * That endpoint already returns `name` computed per the version's `access_version_original_name`
   * permission, so we always send it as the `name` param rather than issuing a separate permissions
   * call per download.
   *
   * @param file The AmsFile (version asset) to download
   * @param options Optional download options (name is computed, not accepted here)
   */
  downloadVersionAsset(file: Objects.AmsFile, options?: {disposition?: string, redirect?: boolean}): void {
    this.downloadFile(file, {
      ...options,
      name: this.getFileNameWithoutExtension(file)
    });
  }

  /**
   * Strip the extension from a file's name, as required by the `name` query param
   * ("custom filename, without extension").
   */
  private getFileNameWithoutExtension(file: Objects.AmsFile): string {
    if (!file?.name) {
      return '';
    }
    const lastDot = file.name.lastIndexOf('.');
    return lastDot > 0 ? file.name.substring(0, lastDot) : file.name;
  }

  /**
   * Build the download URL with all query parameters
   * 
   * @param file The AmsFile to download
   * @param options Optional download options
   * @returns The full download URL
   */
  private buildDownloadUrl(file: Objects.AmsFile, options?: {name?: string, disposition?: string, redirect?: boolean}): string {
    const basePath = `/pub/f/${file.uuid}`;
    const params = new URLSearchParams();
    
    // Required: signature
    params.append('signature', file.access.signature);
    
    // Optional: disposition (default: attachment)
    const disposition = options?.disposition || 'attachment';
    params.append('disposition', disposition);
    
    // Optional: custom filename
    if (options?.name) {
      params.append('name', options.name);
    }
    
    // Optional: redirect (default: true)
    if (options?.redirect !== undefined) {
      params.append('redirect', options.redirect.toString());
    }
    
    const queryString = params.toString();
    const urlPath = queryString ? `${basePath}?${queryString}` : basePath;
    
    return this.urlSrv.buildFiles(urlPath);
  }

  /**
   * Check if a signature has expired
   * 
   * @param expiresAt The expiration date/time
   * @returns true if signature is expired or expiring within 5 seconds
   */
  private isSignatureExpired(expiresAt: Date | string): boolean {
    if (!expiresAt) return true;
    
    const expireTime = new Date(expiresAt).getTime();
    const nowTime = new Date().getTime();
    const bufferMs = 5000; // 5 second buffer
    
    return expireTime < (nowTime + bufferMs);
  }
}
